import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  LinearProgress, 
  Avatar, 
  CardHeader, 
  Card,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { format, formatDistanceToNow, differenceInDays, 
  differenceInHours, differenceInMinutes } from 'date-fns';
import { ru, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TimerIcon from '@mui/icons-material/Timer';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { leaderboardAPI } from '../../services/api';

// Вынести formatTimeLeft вне компонента
const formatTimeLeft = (t, seconds) => {
  if (seconds <= 0) return t('leaderboard:competitions.time.seconds', { count: 0 });
  const days = Math.floor(seconds / (24 * 60 * 60));
  seconds -= days * 24 * 60 * 60;
  const hours = Math.floor(seconds / (60 * 60));
  seconds -= hours * 60 * 60;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  let result = '';
  if (days > 0) result += t('leaderboard:competitions.time.days', { count: days }) + ' ';
  if (hours > 0 || days > 0) result += t('leaderboard:competitions.time.hours', { count: hours }) + ' ';
  if (minutes > 0 || hours > 0 || days > 0) result += t('leaderboard:competitions.time.minutes', { count: minutes }) + ' ';
  if (seconds > 0 && days === 0) result += t('leaderboard:competitions.time.seconds', { count: seconds });
  return result.trim();
};

const CompetitionDetails = ({ competitionId }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation(['leaderboard', 'common']);
  const currentLanguage = i18n.language || 'en';
  
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  
  // Загрузка данных соревнования
  useEffect(() => {
    if (!competitionId) {
      setCompetition(null);
      setLoading(false);
      setError(null);
      return;
    }
    const fetchCompetitionDetails = async () => {
      try {
        setLoading(true);
        const response = await leaderboardAPI.getCompetitionDetails(competitionId);
        setCompetition(response.data);
        setError(null);
      } catch (err) {
        setCompetition(null);
        setError(t('common:errors.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchCompetitionDetails();
  }, [competitionId, t]);

  // Выбор локали и формата даты
  const getDateLocale = useCallback(() => (currentLanguage === 'ru' ? ru : enGB), [currentLanguage]);

  // Обновление информации о времени
  useEffect(() => {
    if (!competition) return;
    
    const updateTime = () => {
      const now = new Date();
      const startDate = new Date(competition.startDate);
      const endDate = new Date(competition.endDate);
      
      // Расчет общей продолжительности соревнования в секундах
      const totalDuration = Math.floor((endDate - startDate) / 1000);
      setTotalSeconds(totalDuration);
      
      let timeLeftText = '';
      let secondsRemaining = 0;
      
      if (now < startDate) {
        // До начала
        secondsRemaining = Math.floor((startDate - now) / 1000);
        timeLeftText = `${t('leaderboard:competitions.time.untilStart')}: ${formatTimeLeft(t, secondsRemaining)}`;
      } else if (now < endDate) {
        // До завершения
        secondsRemaining = Math.floor((endDate - now) / 1000);
        timeLeftText = `${t('leaderboard:competitions.time.untilEnd')}: ${formatTimeLeft(t, secondsRemaining)}`;
      } else {
        // Завершено
        timeLeftText = `${t('leaderboard:competitions.time.finishedAgo', { ago: formatDistanceToNow(endDate, { addSuffix: true, locale: getDateLocale() }) })}`;
        secondsRemaining = 0;
      }
      
      setSecondsLeft(secondsRemaining);
      setTimeLeft(timeLeftText);
    };
    
    // Обновляем сразу при монтировании и затем каждую секунду
    updateTime();
    const timer = setInterval(updateTime, 1000);
    
    return () => clearInterval(timer);
  }, [competition, t, getDateLocale]);

  // Расчет процента завершения соревнования
  const getCompletionPercentage = useCallback(() => {
    if (!competition) return 0;
    const now = new Date();
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);
    if (now < startDate) return 0;
    if (now > endDate) return 100;
    const totalDuration = endDate - startDate;
    const elapsed = now - startDate;
    return Math.min(100, Math.floor((elapsed / totalDuration) * 100));
  }, [competition]);

  // Форматирование длительности соревнования
  const getCompetitionDuration = useCallback(() => {
    if (!competition) return '';
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);
    const days = differenceInDays(endDate, startDate);
    const hours = differenceInHours(endDate, startDate) % 24;
    const minutes = differenceInMinutes(endDate, startDate) % 60;
    let result = '';
    if (days > 0) result += `${days} ${t('common:time.days')} `;
    if (hours > 0) result += `${hours} ${t('common:time.hours')} `;
    if (minutes > 0) result += `${minutes} ${t('common:time.minutes')}`;
    return result.trim();
  }, [competition, t]);

  // Определение статуса соревнования
  const getCompetitionStatus = useCallback(() => {
    if (!competition) return { label: t('leaderboard:competitions.status.unknown'), color: 'default' };
    const now = new Date();
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);
    if (now < startDate) {
      return { label: t('leaderboard:competitions.status.planned'), color: 'warning' };
    } else if (now >= startDate && now <= endDate) {
      return { label: t('leaderboard:competitions.status.active'), color: 'success' };
    } else {
      return { label: t('leaderboard:competitions.status.completed'), color: 'default' };
    }
  }, [competition, t]);

  // Перевод типа соревнования
  const getCompetitionType = useCallback((type) => {
    switch (type) {
      case 'seasonal': return t('leaderboard:competitions.types.seasonal');
      case 'challenge': return t('leaderboard:competitions.types.challenge');
      case 'event': return t('leaderboard:competitions.types.event');
      case 'tournament': return t('leaderboard:competitions.types.tournament');
      default: return t('common:unknown');
    }
  }, [t]);

  // Перевод метрики соревнования
  const getCompetitionMetric = useCallback((metric) => {
    switch (metric) {
      case 'experience': return t('leaderboard:competitions.metrics.experience');
      case 'courses': return t('leaderboard:competitions.metrics.courses');
      case 'tests': return t('leaderboard:competitions.metrics.tests');
      case 'skill': return t('leaderboard:competitions.metrics.skill');
      case 'streak': return t('leaderboard:competitions.metrics.streak');
      default: return t('common:unknown');
    }
  }, [t]);

  // Проверка, является ли пользователь участником
  const isUserParticipant = useCallback(() => {
    if (!competition || !user) return false;
    return competition.participants.some(p => 
      p.userId === user._id || 
      (typeof p.userId === 'object' && p.userId._id === user._id)
    );
  }, [competition, user]);

  // Получение позиции текущего пользователя
  const getUserRank = useCallback(() => {
    if (!competition || !user) return null;
    const participant = competition.participants.find(p => 
      p.userId === user._id || 
      (typeof p.userId === 'object' && p.userId._id === user._id)
    );
    return participant ? participant.rank : null;
  }, [competition, user]);

  if (!competitionId) return null;
  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography mt={2}>{t('common:loading')}</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{t('common:errors.loadError')}</Alert>
      </Box>
    );
  }
  if (!competition) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="warning">{t('common:errors.notFound')}</Alert>
      </Box>
    );
  }

  const status = getCompetitionStatus();
  const completionPercentage = getCompletionPercentage();
  const userRank = getUserRank();

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={1} mb={3}>
        <EmojiEventsIcon />
        {competition.title}
      </Typography>

      <Grid container spacing={3}>
        {/* Информация о соревновании */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              title={t('leaderboard:competitions.detailsBlock.infoTitle')}
              subheader={competition.description}
            />
            
            <Box px={3} pb={3}>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip 
                  label={status.label} 
                  color={status.color} 
                  icon={<AccessTimeIcon />}
                />
                <Chip 
                  label={`${t('leaderboard:competitions.form.typeLabel')}: ${getCompetitionType(competition.type)}`} 
                  variant="outlined" 
                />
                <Chip 
                  label={`${t('leaderboard:competitions.form.metricLabel')}: ${getCompetitionMetric(competition.metric)}`} 
                  variant="outlined" 
                />
                {competition.experienceMultiplier && competition.experienceMultiplier !== 1 && (
                  <Chip 
                    label={`${t('leaderboard:competitions.form.multiplierLabel')}: ${competition.experienceMultiplier}x`} 
                    variant="outlined" 
                    color="secondary"
                  />
                )}
              </Box>
              
              <Box mb={2}>
                <Typography variant="body1" display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarTodayIcon fontSize="small" color="action" />
                  {t('leaderboard:competitions.period')}: {format(new Date(competition.startDate), 'dd MMMM yyyy HH:mm', { locale: getDateLocale() })} - 
                  {format(new Date(competition.endDate), 'dd MMMM yyyy HH:mm', { locale: getDateLocale() })}
                </Typography>
                
                <Typography variant="body1" display="flex" alignItems="center" gap={1} mb={1}>
                  <TimerIcon fontSize="small" color="action" />
                  {t('leaderboard:competitions.duration')}: {getCompetitionDuration()}
                </Typography>
                
                <Typography variant="body1" display="flex" alignItems="center" gap={1} mb={1}>
                  <PeopleIcon fontSize="small" color="action" />
                  {t('leaderboard:competitions.participants')}: {competition.participants?.length || 0}
                </Typography>
              </Box>
              
              {/* Прогресс соревнования */}
              {status.color === 'success' && (
                <Box mb={2}>
                  <Typography variant="body1" fontWeight="bold" mb={1}>
                    {timeLeft}
                  </Typography>
                  <Typography variant="body2" display="flex" justifyContent="space-between">
                    <span>{t('leaderboard:competitions.progress')}:</span>
                    <span>{completionPercentage}%</span>
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={completionPercentage} 
                    sx={{ mt: 1, mb: 1, height: 8, borderRadius: 1 }} 
                  />
                  <Typography variant="caption" color="text.secondary">
                    {status.color === 'success' && totalSeconds > 0 ?
                      t('leaderboard:competitions.timeLeftPercent', { percent: Math.floor((secondsLeft / totalSeconds) * 100) }) :
                      ''}
                  </Typography>
                </Box>
              )}
              
              {/* Награды */}
              <Box mt={3}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={1}>
                  <WorkspacePremiumIcon fontSize="small" color="primary" />
                  {t('leaderboard:competitions.rewards')}
                </Typography>
                
                <Table size="small">
                  <TableBody>
                    {competition.rewards?.experience?.first && (
                      <TableRow>
                        <TableCell>{t('leaderboard:competitions.placeFirst')}</TableCell>
                        <TableCell align="right">{competition.rewards.experience.first} XP</TableCell>
                      </TableRow>
                    )}
                    {competition.rewards?.experience?.second && (
                      <TableRow>
                        <TableCell>{t('leaderboard:competitions.placeSecond')}</TableCell>
                        <TableCell align="right">{competition.rewards.experience.second} XP</TableCell>
                      </TableRow>
                    )}
                    {competition.rewards?.experience?.third && (
                      <TableRow>
                        <TableCell>{t('leaderboard:competitions.placeThird')}</TableCell>
                        <TableCell align="right">{competition.rewards.experience.third} XP</TableCell>
                      </TableRow>
                    )}
                    {competition.rewards?.experience?.participation && (
                      <TableRow>
                        <TableCell>{t('leaderboard:competitions.participation')}</TableCell>
                        <TableCell align="right">{competition.rewards.experience.participation} XP</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        {/* Информация для текущего пользователя */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title={t('leaderboard:competitions.detailsBlock.yourParticipation')} />
            
            <Box px={3} pb={3}>
              {user ? (
                <>
                  {isUserParticipant() ? (
                    <>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {t('common:you')} {t('leaderboard:competitions.yourParticipation')}
                      </Alert>
                      
                      {userRank && (
                        <Box textAlign="center" mb={2}>
                          <Typography variant="h3" color="primary">{userRank}</Typography>
                          <Typography variant="body2">{t('common:you')} {t('leaderboard:competitions.yourPlace')}</Typography>
                        </Box>
                      )}
                      
                      {/* Можно добавить индивидуальный прогресс пользователя здесь */}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('common:you')} {t('leaderboard:competitions.notParticipating')}
                      {status.color === 'success' || status.color === 'warning' ? 
                        `. ${t('leaderboard:competitions.join')}!` : ''}
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  {t('common:login')} {t('leaderboard:competitions.participate')}
                </Alert>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Таблица участников */}
      <Box mt={4}>
        <Typography variant="h5" mb={2} display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon />
          {t('leaderboard:competitions.leaderboard')}
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="10%">{t('leaderboard:competitions.place')}</TableCell>
                <TableCell>{t('leaderboard:competitions.participant')}</TableCell>
                <TableCell align="right">{t('leaderboard:competitions.score')}</TableCell>
                <TableCell align="right">{t('leaderboard:competitions.joinedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {competition.participants && competition.participants.length > 0 ? (
                competition.participants
                  .sort((a, b) => a.rank - b.rank)
                  .map((participant) => {
                    const isCurrentUser = user && 
                      (participant.userId === user._id || 
                      (typeof participant.userId === 'object' && participant.userId._id === user._id));
                    
                    return (
                      <TableRow 
                        key={typeof participant.userId === 'object' ? participant.userId._id : participant.userId} 
                        sx={{ 
                          backgroundColor: isCurrentUser ? 'rgba(0, 123, 255, 0.1)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          {participant.rank === 1 && <WorkspacePremiumIcon color="primary" />}
                          {participant.rank === 2 && <WorkspacePremiumIcon color="secondary" />}
                          {participant.rank === 3 && <WorkspacePremiumIcon color="warning" />}
                          {participant.rank > 3 && participant.rank}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar 
                              src={typeof participant.userId === 'object' ? participant.userId.avatar : null} 
                              sx={{ width: 32, height: 32 }}
                            >
                              {typeof participant.userId === 'object' ? 
                                participant.userId.username?.charAt(0) || t('common:unknownUser') : t('common:unknownUser')}
                            </Avatar>
                            <Typography>
                              {typeof participant.userId === 'object' ? 
                                participant.userId.username : t('common:unknownUser')}
                              {isCurrentUser && ` (${t('common:you')})`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{participant.score}</TableCell>
                        <TableCell align="right">
                          {format(new Date(participant.joinedAt), 'dd MMM yyyy', { locale: getDateLocale() })}
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">{t('leaderboard:competitions.noParticipants')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default CompetitionDetails; 